import React, { useState, useEffect, useRef } from "react";
import { useUserProfile } from "../hooks/useUserProfile";
import { useBuilding } from "../context/BuildingContext";
import {
  LuBuilding2,
  LuChevronDown,
  LuLogOut,
  LuUser,
} from "react-icons/lu";
import RoleSwitcher from "./RoleSwitcher";

const HeaderEmployee = ({ setSelected, onLogout = () => {}, mobile = false }) => {
  const { photoURL, initial } = useUserProfile();
  const { selectedBuilding } = useBuilding();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu]);

  // Close dropdown on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") setShowMenu(false);
    };
    if (showMenu) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => document.removeEventListener("keydown", handleEscape);
  }, [showMenu]);

  return (
    <header style={styles.header} role="banner">
      {/* Badge del edificio: a la izquierda en mobile, centrado en desktop. */}
      <div style={mobile ? { ...styles.left, minWidth: 0, flex: 1 } : styles.left}>
        {mobile && selectedBuilding && (
          <div style={{ ...styles.buildingBadge, minWidth: 0 }}>
            <LuBuilding2 size={14} color="#0080ff" style={{ flexShrink: 0 }} />
            <span style={{ ...styles.buildingName, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedBuilding.name}</span>
          </div>
        )}
      </div>

      {/* Center: Building name (solo desktop) */}
      <div style={styles.center}>
        {!mobile && selectedBuilding && (
          <div style={styles.buildingBadge}>
            <LuBuilding2 size={14} color="#0080ff" />
            <span style={styles.buildingName}>{selectedBuilding.name}</span>
          </div>
        )}
      </div>

      {/* Right: User menu */}
      <div style={styles.right} ref={menuRef}>
        <div
          style={styles.userMenu}
          onClick={() => setShowMenu(!showMenu)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && setShowMenu(!showMenu)}
          aria-expanded={showMenu}
          aria-haspopup="true"
        >
          {photoURL ? (
            <img src={photoURL} alt="Tu perfil" style={styles.avatar} />
          ) : (
            <div style={styles.avatarLetter} aria-hidden="true">{initial}</div>
          )}
          <LuChevronDown size={10} color="#6b7280" />
          {showMenu && (
            <div style={styles.dropdown} role="menu">
              <div
                style={styles.dropdownItem}
                onClick={() => { setSelected("perfil"); setShowMenu(false); }}
                role="menuitem"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter") { setSelected("perfil"); setShowMenu(false); } }}
              >
                <LuUser size={12} style={{ marginRight: 6 }} />
                Mi Perfil
              </div>
              <RoleSwitcher variant="menu" />
              <div style={styles.dropdownDivider}></div>
              <div
                style={styles.dropdownItem}
                onClick={() => { onLogout?.(); setShowMenu(false); }}
                role="menuitem"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter") { onLogout?.(); setShowMenu(false); } }}
              >
                <LuLogOut size={12} style={{ marginRight: 6 }} />
                Cerrar Sesión
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

const styles = {
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: 56,
    padding: "0 16px",
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e5e7eb",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    flexShrink: 0,
    zIndex: 50,
  },
  left: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  center: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
  },
  buildingBadge: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 14px",
    backgroundColor: "#e6f2ff",
    borderRadius: 20,
    border: "1px solid #d0e4ff",
  },
  buildingName: {
    fontSize: 14,
    fontWeight: 400,
    color: "#111827",
  },
  right: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  userMenu: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    cursor: "pointer",
    position: "relative",
    padding: "4px 8px",
    borderRadius: 8,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    objectFit: "cover",
  },
  avatarLetter: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    backgroundColor: "#0080ff",
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 400,
  },
  dropdown: {
    position: "absolute",
    top: "100%",
    right: 0,
    marginTop: 4,
    backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    minWidth: 160,
    zIndex: 200,
    overflow: "hidden",
  },
  dropdownItem: {
    padding: "10px 16px",
    fontSize: 14,
    color: "#374151",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    transition: "background 0.15s",
  },
  dropdownDivider: {
    height: 1,
    backgroundColor: "#e5e7eb",
  },
};

export default HeaderEmployee;
